// Small deterministic RNG (mulberry32). The room stores `state` so a restored room
// continues the same sequence; tests can seed it for reproducible games.

export interface Rng {
  next(): number; // [0, 1)
  int(maxExclusive: number): number;
  pick<T>(arr: readonly T[]): T;
  shuffle<T>(arr: readonly T[]): T[];
  id(len?: number): string;
  state(): number;
}

export function createRng(seed: number): Rng {
  let s = seed >>> 0 || 0x9e3779b9;
  const next = () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const int = (n: number) => Math.floor(next() * n);
  return {
    next,
    int,
    pick: (arr) => arr[int(arr.length)],
    shuffle: (arr) => {
      const a = arr.slice();
      for (let i = a.length - 1; i > 0; i--) {
        const j = int(i + 1);
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    },
    id: (len = 6) => {
      const abc = "abcdefghijkmnopqrstuvwxyz23456789";
      let out = "";
      for (let i = 0; i < len; i++) out += abc[int(abc.length)];
      return out;
    },
    state: () => s,
  };
}

/**
 * The same interface backed by the platform's cryptographic generator. Server rooms use it, so
 * no secret (where the truth sits among the lies, the board, who the spy is) can be predicted from
 * values a player can see, such as ids.
 */
export function createSecureRng(): Rng {
  const buf = new Uint32Array(64);
  let at = buf.length;
  const u32 = () => {
    if (at >= buf.length) {
      crypto.getRandomValues(buf);
      at = 0;
    }
    return buf[at++];
  };
  // 53 random bits → [0, 1)
  const next = () => (u32() * 2 ** 21 + (u32() >>> 11)) / 2 ** 53;
  const int = (n: number) => {
    if (n <= 0) return 0;
    // rejection sampling: no modulo bias
    const limit = Math.floor(2 ** 32 / n) * n;
    for (;;) {
      const x = u32();
      if (x < limit) return x % n;
    }
  };
  return {
    next,
    int,
    pick: (arr) => arr[int(arr.length)],
    shuffle: (arr) => {
      const a = arr.slice();
      for (let i = a.length - 1; i > 0; i--) {
        const j = int(i + 1);
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    },
    id: (len = 6) => {
      const abc = "abcdefghijkmnopqrstuvwxyz23456789";
      let out = "";
      for (let i = 0; i < len; i++) out += abc[int(abc.length)];
      return out;
    },
    state: () => u32(),
  };
}

export function randomSeed(): number {
  if (typeof crypto !== "undefined" && "getRandomValues" in crypto) {
    const a = new Uint32Array(1);
    crypto.getRandomValues(a);
    return a[0];
  }
  return Math.floor(Math.random() * 2 ** 32);
}
